import type { SubtaskOrderEntry, SubtaskOrderSuggestion, TaskCard, TaskColumn, TaskWaiting, TaskWaitingRef } from '@shared/types/tasks';
import { asWaitingRef } from './task-waiting';
import { subtaskWaits, type SubtaskWaits } from '@shared/subtask-graph';
import { orderMoves, sameOrderGraph } from '@shared/subtask-order';

/** Sub-tasks (T-42): the TaskSubtasks list's rules, kept out of the component so they can be tested. */

/** Whether a sub-task is done: in a done column. */
export function isSubtaskDone(task: Pick<TaskCard, 'column_id'>, columns: readonly TaskColumn[]): boolean {
  return columns.some((c) => c.id === task.column_id && c.is_done);
}

/**
 * SF-T-267: a sub-task no longer holds up the ones that wait on it, as the one-at-a-time pick-up counts it: it's done,
 * or has a PR that's open, a draft or merged.
 */
export function isSubtaskBuilt(task: Pick<TaskCard, 'column_id' | 'prs'>, columns: readonly TaskColumn[]): boolean {
  return isSubtaskDone(task, columns) || task.prs.some((pr) => pr.status === 'open' || pr.status === 'draft' || pr.status === 'merged');
}

/**
 * SF-T-301: the sub-tasks Build all (and Build one at a time) still has to build: not built (isSubtaskBuilt: done, or an
 * open, draft or merged PR) and not being built now (a run going on it, here or in `activeRuns`, or with the AI builder).
 */
export function subtasksToBuild<T extends Pick<TaskCard, 'id' | 'column_id' | 'prs' | 'run_going' | 'waiting_on'>>(
  subtasks: readonly T[],
  columns: readonly TaskColumn[],
  activeRuns: { has(id: string): boolean } = new Set<string>()
): T[] {
  return subtasks.filter((t) => !isSubtaskBuilt(t, columns) && !t.run_going && !activeRuns.has(t.id) && !waitsOnAi(t.waiting_on));
}

/**
 * SF-T-279: a sub-task is settled for the ones waiting on it (shown in green): it's done, or it's built (a PR) and nothing
 * is working on it now: no run going (`run_going`, from the task detail) and not waiting on the AI builder for a follow-up.
 */
export function isSubtaskUnlocked(task: Pick<TaskCard, 'column_id' | 'prs' | 'run_going' | 'waiting_on'>, columns: readonly TaskColumn[]): boolean {
  if (isSubtaskDone(task, columns)) return true;
  return isSubtaskBuilt(task, columns) && !task.run_going && !waitsOnAi(task.waiting_on);
}

/**
 * SF-T-279: where a sub-task stands, as the graph shows it, first that applies: done (in a done column, or its PR merged);
 * needs a person (waiting on someone: its questions, a parked live run, a failed run); building (a run going on it, or
 * with the AI builder);
 * PR ready (its PR is up and nothing is working on it: ready to merge); can start (what it waits on is built); waiting.
 */
export type SubtaskNodeState = 'done' | 'building' | 'needs_person' | 'pr_ready' | 'can_start' | 'waiting';

export function subtaskNodeState(
  task: Pick<TaskCard, 'column_id' | 'prs' | 'run_going' | 'waiting_on'>,
  columns: readonly TaskColumn[],
  /** What it waits on in the graph (subtaskEdges), as cards. */
  deps: readonly Pick<TaskCard, 'column_id' | 'prs'>[]
): SubtaskNodeState {
  if (isSubtaskDone(task, columns) || task.prs.some((pr) => pr.status === 'merged')) return 'done';
  // Someone is waited on first: a live run parked for its starter, or its questions, is a run still going too.
  if (task.waiting_on.some((w) => w.target !== 'ai')) return 'needs_person';
  if (task.run_going || waitsOnAi(task.waiting_on)) return 'building';
  if (task.prs.some((pr) => pr.status === 'open' || pr.status === 'draft')) return 'pr_ready';
  return deps.every((d) => isSubtaskBuilt(d, columns)) ? 'can_start' : 'waiting';
}

/** Where ticking (the first done column) or unticking (the first that isn't done) moves a sub-task. */
export function tickColumn(columns: TaskColumn[], done: boolean): TaskColumn | undefined {
  return [...columns].sort((a, b) => a.position - b.position).find((c) => c.is_done === done);
}

/** Whether the sub-task waits on the AI builder already (it was handed over, or a run asked it). */
export function waitsOnAi(waiting: TaskWaiting[]): boolean {
  return waiting.some((w) => w.target === 'ai');
}

/** Its Waiting on with the AI builder added, as the API takes it: the AI builder picks it up (T-2). */
export function withAiBuilder(waiting: TaskWaiting[]): TaskWaitingRef[] {
  return waitsOnAi(waiting) ? waiting.map(asWaitingRef) : [...waiting.map(asWaitingRef), { target: 'ai' }];
}

/** Sub-tasks a run can stack on: those with an open PR, newest number first. */
export function stackableSubtasks(subtasks: TaskCard[]): TaskCard[] {
  return subtasks.filter((t) => t.prs.some((p) => p.status === 'open' || p.status === 'draft')).sort((a, b) => b.number - a.number);
}

/** SF-T-132: the tasks a task can go under (MakeSubtaskDialog): top-level (one level), open, not itself; newest first. */
export function parentChoices(tasks: TaskCard[], taskId: string): TaskCard[] {
  return tasks.filter((t) => t.id !== taskId && !t.parent && !t.archived && !t.done_at).sort((a, b) => b.number - a.number);
}

/** SF-T-263: the open sub-tasks picked for the one-at-a-time chain that have no PR yet (0 with no pick). */
export function chainLeftToBuild(subtasks: readonly TaskCard[], columns: readonly TaskColumn[]): number {
  return subtasks.filter((t) => t.in_chain && !isSubtaskDone(t, columns) && !t.prs.some((pr) => pr.status === 'open' || pr.status === 'draft' || pr.status === 'merged')).length;
}

/**
 * SF-T-269: the breaks the one-at-a-time chain has reached (task.chain_break, in the chain: picked, or any with no
 * pick) and not passed yet, as the pick-up reads them: each has a PR that's open, a draft or merged, or is done. With the parent off the
 * AI builder, the chain stopped at them; Continue hands it back and marks them passed.
 */
export function reachedBreaks(subtasks: readonly TaskCard[], columns: readonly TaskColumn[]): TaskCard[] {
  const picked = subtasks.some((t) => t.in_chain);
  return subtasks.filter(
    (t) =>
      t.chain_break &&
      !t.chain_break_passed_at &&
      !t.draft &&
      (!picked || t.in_chain) &&
      (isSubtaskDone(t, columns) || t.prs.some((pr) => pr.status === 'open' || pr.status === 'draft' || pr.status === 'merged'))
  );
}

type GraphNode = Pick<TaskCard, 'id' | 'after' | 'draft' | 'in_chain'>;

/** SF-T-268: what a sub-task waits on in the graph: the ones it names, or (naming none) one before it in the list. */
export type SubtaskEdges = SubtaskWaits;

/**
 * SF-T-268: what each sub-task waits on as the one-at-a-time pick-up reads a graph (subtaskWaits, shared with
 * worker runs/one-at-a-time.ts): the sub-tasks of the list it names, or, naming none, the nearest one before it in the
 * chain's order (the picked ones when there's a pick, else all; never drafts) that doesn't already wait on it. No
 * edges until one names another (`graph`), as the pick-up has none.
 */
export function subtaskEdges(subtasks: readonly GraphNode[], graph = hasSubtaskGraph(subtasks)): Map<string, SubtaskEdges> {
  if (!graph) return new Map(subtasks.map((t) => [t.id, { after: [], implicit: false }]));
  const picked = subtasks.some((t) => t.in_chain);
  const order = subtasks.filter((t) => !t.draft && (!picked || t.in_chain)).map((t) => t.id);
  return subtaskWaits(
    subtasks.map((t) => ({ id: t.id, after: (t.after ?? []).map((d) => d.task_id) })),
    order
  );
}

/**
 * SF-T-268: each sub-task's lane in the dependency graph: 0 for one that waits on nothing, else one past the furthest
 * one it waits on (subtaskEdges). A loop (the server refuses them) is cut.
 */
export function subtaskLanes(subtasks: readonly GraphNode[]): Map<string, number> {
  const edges = subtaskEdges(subtasks);
  const lanes = new Map<string, number>();
  const visiting = new Set<string>();
  const lane = (id: string): number => {
    const known = lanes.get(id);
    if (known !== undefined) return known;
    if (visiting.has(id)) return -1;
    visiting.add(id);
    const deps = edges.get(id)?.after ?? [];
    const value = deps.length ? Math.max(...deps.map(lane)) + 1 : 0;
    visiting.delete(id);
    lanes.set(id, Math.max(value, 0));
    return lanes.get(id)!;
  };
  for (const t of subtasks) lane(t.id);
  return lanes;
}

/** SF-T-268: whether any sub-task in the list waits on another of it (the list draws the graph only then). */
export function hasSubtaskGraph(subtasks: readonly Pick<TaskCard, 'id' | 'after'>[]): boolean {
  const ids = new Set(subtasks.map((t) => t.id));
  return subtasks.some((t) => (t.after ?? []).some((d) => ids.has(d.task_id)));
}

/**
 * SF-T-268: the sub-tasks that name `taskId` as one they wait on, directly or through others: it can't wait on one of
 * them (a loop, which the server refuses). The list's order never makes one (subtaskWaits), so only names count.
 */
export function waitingOn(subtasks: readonly Pick<TaskCard, 'id' | 'after'>[], taskId: string): Set<string> {
  const dependents = new Map<string, string[]>();
  for (const t of subtasks) for (const d of t.after ?? []) dependents.set(d.task_id, [...(dependents.get(d.task_id) ?? []), t.id]);
  const found = new Set<string>();
  const queue = [...(dependents.get(taskId) ?? [])];
  while (queue.length) {
    const id = queue.shift()!;
    if (found.has(id) || id === taskId) continue;
    found.add(id);
    queue.push(...(dependents.get(id) ?? []));
  }
  return found;
}

/**
 * SF-T-268: the sub-task before each in the chain's order (the picked ones when there's a pick, never drafts): one that
 * names only that one waits on it anyway, so its 'after …' is left out.
 */
export function subtaskBefore(subtasks: readonly GraphNode[]): Map<string, string> {
  const picked = subtasks.some((t) => t.in_chain);
  const order = subtasks.filter((t) => !t.draft && (!picked || t.in_chain));
  return new Map(order.slice(1).map((t, i) => [t.id, order[i].id]));
}

/** SF-T-271: the sub-tasks as a suggested order reads them (not drafts or archived ones), in the list's order. */
export function orderGraphOf(subtasks: readonly Pick<TaskCard, 'id' | 'after' | 'chain_break' | 'draft' | 'archived'>[]): SubtaskOrderEntry[] {
  return subtasks.filter((s) => !s.draft && !s.archived).map((s) => ({ task_id: s.id, after: s.after.map((a) => a.task_id), chain_break: s.chain_break }));
}

/**
 * SF-T-271: the AI's suggested order can't be applied as it is, as the server sees it: the sub-tasks changed since
 * (added, removed, moved, or their waits or breaks), one it changes or its order moves has a PR or is done, or it changes a break while
 * they aren't built one at a time any more.
 */
export function isOrderSuggestionStale(
  suggestion: SubtaskOrderSuggestion,
  subtasks: readonly (Parameters<typeof orderGraphOf>[0][number] & Pick<TaskCard, 'column_id' | 'prs'>)[],
  columns: readonly TaskColumn[],
  oneAtATime: boolean
): boolean {
  if (!sameOrderGraph(suggestion.graph, orderGraphOf(subtasks))) return true;
  const built = (s: Pick<TaskCard, 'column_id' | 'prs'>) => isSubtaskDone(s, columns) || s.prs.some((p) => p.status !== 'closed');
  // SF-T-268: what it changes, and what its list order moves.
  if ([...suggestion.changes.map((c) => c.task_id), ...orderMoves(suggestion)].some((id) => { const s = subtasks.find((t) => t.id === id); return !s || built(s); })) return true;
  return !oneAtATime && suggestion.changes.some((c) => c.chain_break !== c.was_break);
}

/**
 * SF-T-271: the sub-tasks with every change of a suggestion applied, as accepting it does: their waits and breaks, and
 * the list's order (its sub-tasks first, in it; the rest after, as they were).
 */
export function withOrderSuggestion<T extends Pick<TaskCard, 'id' | 'after' | 'chain_break' | 'chain_break_passed_at'>>(subtasks: T[], suggestion: SubtaskOrderSuggestion): T[] {
  const changes = new Map(suggestion.changes.map((c) => [c.task_id, c]));
  const changed = subtasks.map((s) => {
    const c = changes.get(s.id);
    if (!c) return s;
    return { ...s, after: c.after, ...(c.chain_break !== s.chain_break ? { chain_break: c.chain_break, chain_break_passed_at: null } : {}) };
  });
  if (!suggestion.order?.length) return changed;
  const order = suggestion.order.map((o) => o.task_id);
  const at = (id: string) => (order.includes(id) ? order.indexOf(id) : order.length + changed.findIndex((s) => s.id === id));
  return [...changed].sort((a, b) => at(a.id) - at(b.id));
}

/** SF-T-271: where the suggestion would put each row it moves (1-based, among the sub-tasks it read). */
export function suggestedPositions(suggestion: SubtaskOrderSuggestion, subtasks: readonly Pick<TaskCard, 'id' | 'draft' | 'archived'>[]): Map<string, number> {
  const now = subtasks.filter((s) => !s.draft && !s.archived).map((s) => s.id);
  const order = suggestion.order?.map((o) => o.task_id) ?? [];
  return new Map(order.flatMap((id, i) => (now.indexOf(id) === i ? [] : [[id, i + 1] as [string, number]])));
}

/** SF-T-271: the waits a suggestion adds to the graph (`dep>task`, read as the pick-up does), to draw in its colour. */
export function newOrderEdges(before: readonly GraphNode[], after: readonly GraphNode[]): Set<string> {
  const keys = (list: readonly GraphNode[]) =>
    new Set([...subtaskEdges(list, true)].flatMap(([task, e]) => e.after.map((dep) => `${dep}>${task}`)));
  const was = keys(before);
  return new Set([...keys(after)].filter((k) => !was.has(k)));
}
